/**
 * Hook for bulk-inserting auth-status-change comments into Postgres.
 * @module useAuthStatusCommentsInsert
 */

import { useCallback, useEffect } from "react";

import {
  bulkInsertAuthStatusComments,
  BulkInsertAuthStatusCommentsResponse,
} from "../../api/postgreSQL/postCalls/bulkInsertAuthStatusComments";
import { AuthStatusChangeComments } from "../../data-model/authStatusChangeComments";
import { useAbortableFetch } from "../useAbortableFetch";

/** Parameters for the {@link useAuthStatusCommentsInsert} hook. */
interface UseAuthStatusCommentsInsertProps {
  /** PA order ID the comments belong to. */
  orderId: string;
  /** Organisation ID scoping the insert. */
  orgId: string;
}

/**
 * Provides an abortable bulk-insert function for auth-status-change comments.
 *
 * Does not auto-fetch on mount. Exposes a `runInsert(comments)` function that
 * callers invoke with an array of {@link AuthStatusChangeComments}.
 * In-flight requests are automatically cancelled on unmount.
 *
 * @param props - Order ID and org ID identifying the target order.
 * @returns runInsert - Triggers the bulk insert with the given comments.
 * @returns abort     - Manually cancel any in-flight insert request.
 * @returns data      - The insert response (null until a request completes).
 * @returns loading   - Whether an insert request is in progress.
 * @returns error     - Error object if the insert failed.
 */
export const useAuthStatusCommentsInsert = (
  props: UseAuthStatusCommentsInsertProps,
) => {
  const { orderId, orgId } = props;

  const fetchFn = useCallback(
    async (signal: AbortSignal, comments: AuthStatusChangeComments[]) => {
      return bulkInsertAuthStatusComments(
        {
          order_id: orderId,
          org_id: orgId,
          data: comments,
        },
        signal,
      );
    },
    [orderId, orgId],
  );

  const { runFetch, abort, data, loading, error } =
    useAbortableFetch<BulkInsertAuthStatusCommentsResponse>(fetchFn);

  useEffect(() => {
    return () => abort();
  }, [abort]);

  const runInsert = useCallback(
    async (comments: AuthStatusChangeComments[]) => runFetch(comments),
    [runFetch],
  );

  return { runInsert, abort, data, loading, error };
};
